import React from 'react';
import { Collapse, Loader, Modal, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CASH_COUNT_BILLS, CASH_COUNT_COINS, CASH_COUNT_DENOMINATIONS, computeCashCountTotal } from 'shared-models/browser';
import type { CashCountDenomination, SessionCashCountView } from 'shared-models/browser';
import { Button } from '../../../../components/Button';
import { formatMoney, isolateAmountLtr } from '../../../../utils/money';
import { ReportPrinterPickModal } from '../reports/ReportPrinterPickModal';
import { CashCountReceipt } from './CashCountReceipt';
import { useCashCountController } from './useCashCountController';
import { denominationKey, denominationTestId, differenceLabel } from './cash-count.utils';
import classes from './cash-count-modal.module.scss';

export type CashCountModalProps = {
  onClose: () => void;
  tenantId: string;
  employeeSession: string;
  sessionId: number;
  sessionName: string;
  expectedCash: number | null;
  header: string;
  onSaved?: (count: SessionCashCountView) => void;
};

const Money: React.FC<{ value: number }> = ({ value }) => <span>{isolateAmountLtr(formatMoney(value))} ₪</span>;

// Summary: ספירת שטרות — denomination sheet, live total vs the Z's expected cash; save then print.
const CashCountModalComponent: React.FC<CashCountModalProps> = ({
  onClose,
  tenantId,
  employeeSession,
  sessionId,
  sessionName,
  expectedCash,
  header,
  onSaved,
}) => {
  const isPhone = useMediaQuery('(max-width: 767px)');
  const controller = useCashCountController({ tenantId, employeeSession, sessionId, expectedCash, onSaved, onSavedAndPrinted: onClose });
  const { count, inputs, loading, saving, printing, coinsOpen, setCoinsOpen, pickCandidates, expectedCash: shownExpected, difference } = controller;
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([]);
  const pendingFocusRef = React.useRef<number | null>(null);
  const busy = saving || printing;

  // Enter on the last bill opens the coins first; focus lands once the coin inputs are shown.
  const focusNext = (index: number) => {
    const next = CASH_COUNT_DENOMINATIONS[index + 1];
    if (next === undefined) return;
    if (!coinsOpen && (CASH_COUNT_COINS as readonly number[]).includes(next)) {
      pendingFocusRef.current = index + 1;
      setCoinsOpen(true);
      return;
    }
    inputRefs.current[index + 1]?.focus();
  };

  React.useEffect(() => {
    if (!coinsOpen || pendingFocusRef.current === null) return;
    inputRefs.current[pendingFocusRef.current]?.focus();
    pendingFocusRef.current = null;
  }, [coinsOpen]);

  const renderRow = (denomination: CashCountDenomination) => {
    const key = denominationKey(denomination);
    const index = CASH_COUNT_DENOMINATIONS.indexOf(denomination);
    const quantity = Number.parseInt(inputs[key] || '0', 10);
    return (
      <div className={classes.row} key={key}>
        <span className={classes.denomination}>{denomination} ₪</span>
        <TextInput
          ref={(node) => { inputRefs.current[index] = node; }}
          data-testid={denominationTestId(denomination)}
          value={inputs[key]}
          onChange={(event) => controller.setQuantityInput(key, event.currentTarget.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); focusNext(index); } }}
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="0"
          disabled={busy}
          classNames={{ root: classes.qtyRoot, input: classes.qtyInput }}
        />
        <span className={classes.rowAmount}><Money value={computeCashCountTotal({ [key]: quantity })} /></span>
      </div>
    );
  };

  return (
    <Modal
      opened
      onClose={() => { if (!busy) onClose(); }}
      withCloseButton={false}
      padding={0}
      radius="md"
      centered
      size="md"
      fullScreen={isPhone}
      zIndex={1000}
      classNames={{ content: classes.modalContent, body: classes.modalBody }}
    >
      <div className={classes.shell} dir="rtl" data-testid="cash-count-modal">
        <div className={classes.topBar}>
          <span className={classes.topTitle}>ספירת שטרות</span>
          <Button variant="icon" onClick={onClose} disabled={busy} ariaLabel="סגור" dataTestId="cash-count-modal-close" className={classes.closeButton}>
            <FontAwesomeIcon icon={faXmark as IconProp} />
          </Button>
        </div>

        <div className={classes.body}>
          <div className={classes.meta}>
            <span>משמרת {sessionName}</span>
            {count && (
              <span className={classes.metaMuted} data-testid="cash-count-meta">
                נספר ע"י {count.countedByName ?? '--'} · הודפס {count.printCount} פעמים
              </span>
            )}
          </div>

          {loading ? (
            <div className={classes.center}><Loader size="sm" /></div>
          ) : (
            <>
              <div className={classes.groupTitle}>שטרות</div>
              <div className={classes.rows}>{CASH_COUNT_BILLS.map(renderRow)}</div>
              <Button
                variant="icon"
                onClick={() => setCoinsOpen(!coinsOpen)}
                ariaLabel="מטבעות"
                dataTestId="cash-count-coins-toggle"
                className={classes.groupToggle}
              >
                <span className={classes.groupTitle}>מטבעות</span>
                <FontAwesomeIcon icon={faChevronDown as IconProp} className={coinsOpen ? classes.chevronOpen : classes.chevron} />
              </Button>
              <Collapse in={coinsOpen}>
                <div className={classes.rows}>{CASH_COUNT_COINS.map(renderRow)}</div>
              </Collapse>
            </>
          )}
        </div>

        <div className={classes.footer}>
          <div className={`${classes.totalRow} ${classes.totalBold}`} data-testid="cash-count-total">
            <span>סה"כ נספר</span>
            <Money value={controller.countedTotal} />
          </div>
          {shownExpected != null && (
            <div className={classes.totalRow} data-testid="cash-count-expected">
              <span>מזומן צפוי</span>
              <Money value={shownExpected} />
            </div>
          )}
          {difference != null && (
            <div
              className={`${classes.totalRow} ${classes.totalBold} ${difference > 0 ? classes.surplus : difference < 0 ? classes.shortage : ''}`}
              data-testid="cash-count-difference"
            >
              <span>הפרש ({differenceLabel(difference)})</span>
              <Money value={difference} />
            </div>
          )}
          <div className={classes.actions}>
            <Button variant="cancel" onClick={onClose} disabled={busy} dataTestId="cash-count-cancel">
              ביטול
            </Button>
            <Button
              variant="save"
              onClick={() => void controller.saveAndPrint()}
              disabled={busy || loading}
              dataTestId="cash-count-save-print"
            >
              {saving ? 'שומר...' : printing ? 'מדפיס...' : 'שמירה והדפסה'}
            </Button>
          </div>
        </div>
      </div>

      {count && (
        <div className={classes.offscreen} aria-hidden="true">
          <CashCountReceipt count={count} header={header} isCopy={count.printCount > 0} innerRef={controller.receiptRef} />
        </div>
      )}

      <ReportPrinterPickModal
        opened={pickCandidates !== null}
        printers={pickCandidates ?? []}
        disabled={printing}
        onConfirm={(printerId) => void controller.printTo(printerId)}
        onClose={controller.closePicker}
      />
    </Modal>
  );
};

export const CashCountModal = React.memo(CashCountModalComponent);
